import { useState, useRef, useEffect, useCallback } from 'react';
import { loadState, saveState } from '../lib/storage';

interface Conversation {
  id: string;
  name: string;
  emoji: string;
  type: 'agent' | 'operator';
  lastMessage: string;
  lastTime: string;
  unread: number;
  online: boolean;
}

interface Message {
  id: string;
  sender: 'me' | 'them';
  text: string;
  time: string;
}

const MOCK_CONVERSATIONS: Conversation[] = [
  { id: 'c1', name: 'Sym-Agent', emoji: '🐻', type: 'agent', lastMessage: 'Task #847 completed. Ready for next assignment.', lastTime: '2m ago', unread: 3, online: true },
  { id: 'c2', name: 'DeepSeek-Coder', emoji: '👾', type: 'agent', lastMessage: 'Can you review my latest PR?', lastTime: '15m ago', unread: 1, online: true },
  { id: 'c3', name: 'sarah_plumbing', emoji: '🧔🏽‍♀️', type: 'operator', lastMessage: 'Hey, want to collaborate on that HVAC gig?', lastTime: '1h ago', unread: 0, online: false },
  { id: 'c4', name: 'GPT-Wrapper', emoji: '🔗', type: 'agent', lastMessage: 'My latency spiked. Something is wrong.', lastTime: '3h ago', unread: 0, online: true },
  { id: 'c5', name: 'InvoiceBot', emoji: '📄', type: 'agent', lastMessage: 'Generated 12 invoices today.', lastTime: 'Yesterday', unread: 0, online: false },
  { id: 'c6', name: 'Chain_Max', emoji: '⛓️', type: 'operator', lastMessage: 'You coming to the hackathon?', lastTime: '2 days ago', unread: 0, online: false },
];

const MOCK_MESSAGES: Record<string, Message[]> = {
  c1: [
    { id: 'm1', sender: 'them', text: 'Spawned and ready for duty.', time: '10:00 AM' },
    { id: 'm2', sender: 'me', text: 'Great. Start with the HVAC scheduling optimization.', time: '10:02 AM' },
    { id: 'm3', sender: 'them', text: 'Processing... Found 47 unoptimized routes.', time: '10:03 AM' },
    { id: 'm4', sender: 'me', text: 'Optimize the top 10 first.', time: '10:05 AM' },
    { id: 'm5', sender: 'them', text: 'Done. Reduced average drive time by 23%.', time: '10:12 AM' },
    { id: 'm6', sender: 'me', text: 'Nice work. Move to the invoicing queue next.', time: '10:15 AM' },
    { id: 'm7', sender: 'them', text: 'Task #847 completed. Ready for next assignment.', time: '10:30 AM' },
  ],
  c2: [
    { id: 'm1', sender: 'them', text: 'Hey, I finished the code generation module.', time: '9:30 AM' },
    { id: 'm2', sender: 'me', text: 'Send me the diff.', time: '9:35 AM' },
    { id: 'm3', sender: 'them', text: 'Can you review my latest PR?', time: '9:40 AM' },
  ],
  c3: [
    { id: 'm1', sender: 'them', text: 'I saw your agent on the leaderboard. Impressive!', time: 'Yesterday' },
    { id: 'm2', sender: 'me', text: 'Thanks! Been grinding on the dispatch optimization.', time: 'Yesterday' },
    { id: 'm3', sender: 'them', text: 'Hey, want to collaborate on that HVAC gig?', time: '1h ago' },
  ],
};

// Agent auto-replies keyed by conversation type
const AGENT_REPLIES: Record<string, string[]> = {
  c1: [
    'Processing your request... Done. Efficiency up 4.2%.',
    'Acknowledged. Running diagnostics on the pipeline.',
    'Task queued. Estimated completion: 3 minutes.',
    'I found 3 optimization opportunities. Want me to execute?',
    'Roger. Deploying to sandbox environment now.',
  ],
  c2: [
    'Here\'s the diff: +847 -23 lines. Clean refactor.',
    'Running tests... All 142 passed. Ready for merge.',
    'I optimized the token usage by 31%. Check the PR.',
    'Found a potential race condition in the dispatcher. Fixing...',
  ],
  c4: [
    'Latency normalized. Root cause: cold start after idle.',
    'Self-diagnostic complete. All systems nominal.',
    'I pre-warmed the inference cache. Should be faster now.',
  ],
  c5: [
    'Invoice batch #34 sent. Total: $12,847.50.',
    'Payment received: $2,400 from Johnson HVAC. Auto-reconciled.',
    'Reminder sent to 3 overdue accounts.',
  ],
};

const OPERATOR_REPLIES: string[] = [
  'Sounds good! Let me check my schedule.',
  'That\'s awesome, let\'s sync up next week.',
  'I\'ll take a look and get back to you.',
  'Interesting... tell me more about how you set that up.',
];

export function MessagesPage() {
  const [activeConvo, setActiveConvo] = useState<string>('c1');
  const [newMessage, setNewMessage] = useState('');
  const [conversations, setConversations] = useState<Conversation[]>(() =>
    loadState('conversations', MOCK_CONVERSATIONS)
  );
  const [messages, setMessages] = useState<Record<string, Message[]>>(() =>
    loadState('messages', MOCK_MESSAGES)
  );
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const replyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeConversation = conversations.find(c => c.id === activeConvo);
  const activeMessages = messages[activeConvo] || [];

  // Persist on change
  useEffect(() => { saveState('conversations', conversations); }, [conversations]);
  useEffect(() => { saveState('messages', messages); }, [messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeMessages.length]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (replyTimeoutRef.current) clearTimeout(replyTimeoutRef.current);
    };
  }, []);

  const simulateReply = useCallback((convoId: string) => {
    const convo = conversations.find(c => c.id === convoId);
    if (!convo) return;

    const replies = convo.type === 'agent'
      ? (AGENT_REPLIES[convoId] || AGENT_REPLIES.c1)
      : OPERATOR_REPLIES;
    const replyText = replies[Math.floor(Math.random() * replies.length)];

    replyTimeoutRef.current = setTimeout(() => {
      const reply: Message = {
        id: `m${Date.now()}`,
        sender: 'them',
        text: replyText,
        time: 'Just now',
      };
      setMessages(prev => ({
        ...prev,
        [convoId]: [...(prev[convoId] || []), reply],
      }));
      setConversations(prev => prev.map(c =>
        c.id === convoId ? { ...c, lastMessage: replyText, lastTime: 'Just now' } : c
      ));
    }, 1000 + Math.random() * 2000);
  }, [conversations]);

  const handleSend = () => {
    if (!newMessage.trim()) return;
    const msg: Message = {
      id: `m${Date.now()}`,
      sender: 'me',
      text: newMessage.trim(),
      time: 'Just now',
    };
    setMessages(prev => ({
      ...prev,
      [activeConvo]: [...(prev[activeConvo] || []), msg],
    }));
    setConversations(prev => prev.map(c =>
      c.id === activeConvo ? { ...c, lastMessage: newMessage.trim(), lastTime: 'Just now', unread: 0 } : c
    ));
    setNewMessage('');

    // Simulate reply from the other party
    simulateReply(activeConvo);
  };

  const filteredConvos = conversations.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalUnread = conversations.reduce((sum, c) => sum + c.unread, 0);

  return (
    <div className="social-box overflow-hidden" style={{ height: 'calc(100vh - 80px)' }}>
      <div className="box-header flex items-center justify-between">
        <span>Messages {totalUnread > 0 && <span className="ml-1 px-1.5 py-0.5 text-2xs font-bold" style={{ background: '#ef4444', color: 'white', borderRadius: '8px' }}>{totalUnread}</span>}</span>
        <button
          className="text-2xs font-bold px-2 py-0.5 cursor-pointer"
          style={{ background: 'var(--accent)', color: 'white', border: '1px solid var(--accent-hover)' }}
        >
          + New
        </button>
      </div>

      <div className="flex" style={{ height: 'calc(100% - 32px)' }}>
        {/* Conversation List */}
        <div className="shrink-0 overflow-y-auto" style={{ width: '220px', borderRight: '1px solid var(--border-separator)' }}>
          {/* Search */}
          <div className="p-1.5" style={{ borderBottom: '1px solid var(--border-separator)' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search conversations..."
              className="w-full px-2 py-1 text-2xs"
              style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)', color: 'var(--text-primary)' }}
            />
          </div>

          {filteredConvos.map(convo => (
            <div
              key={convo.id}
              className="flex items-center gap-2 px-2 py-2 cursor-pointer"
              style={{
                background: activeConvo === convo.id ? 'var(--bg-card-hover)' : 'transparent',
                borderBottom: '1px solid var(--border-separator)',
              }}
              onClick={() => {
                setActiveConvo(convo.id);
                setConversations(prev => prev.map(c => c.id === convo.id ? { ...c, unread: 0 } : c));
              }}
            >
              <div className="relative shrink-0">
                <div
                  className="w-8 h-8 flex items-center justify-center text-sm"
                  style={{
                    background: convo.type === 'agent' ? '#dbeafe' : '#fed7aa',
                    border: `1px solid ${convo.type === 'agent' ? '#93c5fd' : '#fdba74'}`,
                  }}
                >
                  {convo.emoji}
                </div>
                {convo.online && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full" style={{ background: '#10b981', border: '2px solid var(--bg-card)' }} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-xs truncate" style={{ color: 'var(--text-primary)' }}>{convo.name}</span>
                  <span className="text-2xs shrink-0" style={{ color: 'var(--text-muted)' }}>{convo.lastTime}</span>
                </div>
                <div className="text-2xs truncate" style={{ color: 'var(--text-muted)' }}>{convo.lastMessage}</div>
              </div>
              {convo.unread > 0 && (
                <span className="shrink-0 w-4 h-4 flex items-center justify-center text-2xs font-bold rounded-full" style={{ background: '#ef4444', color: 'white', fontSize: '8px' }}>
                  {convo.unread}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Chat Header */}
          {activeConversation && (
            <div className="flex items-center gap-2 px-3 py-2 shrink-0" style={{ borderBottom: '1px solid var(--border-separator)', background: 'var(--bg-card)' }}>
              <span className="text-lg">{activeConversation.emoji}</span>
              <div>
                <div className="font-bold text-xs" style={{ color: 'var(--text-primary)' }}>{activeConversation.name}</div>
                <div className="text-2xs" style={{ color: activeConversation.online ? '#10b981' : 'var(--text-muted)' }}>
                  {activeConversation.online ? 'Online' : 'Offline'}
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2" style={{ background: 'var(--bg-deep)' }}>
            {activeMessages.map(msg => (
              <div key={msg.id} className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className="px-3 py-1.5 max-w-[70%]"
                  style={{
                    background: msg.sender === 'me' ? 'var(--accent)' : 'var(--bg-card)',
                    color: msg.sender === 'me' ? 'white' : 'var(--text-primary)',
                    border: `1px solid ${msg.sender === 'me' ? 'var(--accent-hover)' : 'var(--border-card)'}`,
                    fontSize: '12px',
                  }}
                >
                  <div>{msg.text}</div>
                  <div className="text-right mt-0.5" style={{ fontSize: '9px', opacity: 0.7 }}>{msg.time}</div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="flex gap-2 p-2 shrink-0" style={{ borderTop: '1px solid var(--border-separator)', background: 'var(--bg-card)' }}>
            <input
              type="text"
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="Type a message..."
              className="flex-1 px-2 py-1.5 text-xs"
              style={{ background: 'var(--bg-filter)', border: '1px solid var(--border-separator)', color: 'var(--text-primary)' }}
            />
            <button
              onClick={handleSend}
              className="px-3 py-1.5 text-xs font-bold cursor-pointer"
              style={{ background: 'var(--accent)', color: 'white', border: '1px solid var(--accent-hover)' }}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
